import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { Users, DollarSign, Clock, CalendarRange, Download, Truck } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { tenantTodayDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtCurrency, fmtHours } from '../../lib/display-labels';
import { csvCell, csvMoney } from '../../lib/csv';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import PageHeaderBand, { type PageHeaderStat } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
} from '../../lib/load-failure';
import {
  payrollGross,
  type OperationsPayrollRow,
  type PayrollSummaryReportData,
  type TruckingPayrollRow,
} from '../../types/payroll-summary-report';

/** @see fmtCurrency — this is a pay document; absence renders '—', not '$0.00'. */
const fmt = (n: number | string | null | undefined) => fmtCurrency(n);

/* `fmtHours` and the two CSV cell helpers are IMPORTED, not written here.
   `lib/display-labels.ts` already rejects null / undefined / '' by identity
   before coercing — `Number(null)` and `Number('')` are both 0, which is the
   whole bug — and `lib/csv.ts` owns the different contract a spreadsheet cell
   needs for the same rule: an absent figure is an EMPTY cell there, because a
   '—' sums as zero in Excel. Two spellings of one rule is how one of them
   grows a `|| 0`. */

/**
 * Timesheet status → a saturated signal FILL with `text-fi-signal-ink` on it.
 *
 * EXHAUSTIVE OVER THE FOUR VALUES `employee_timesheets.status` TAKES, and the
 * hues are grouped by MEANING rather than one per value — there are five signal
 * hues and no sixth is added for a page. `approved` and `exported` both mean
 * the office has committed to the money and share green; `draft` and `active`
 * both mean it has not and share amber. The badge text is what separates the
 * members of each pair.
 *
 * An unmapped value gets a slate pill reading '—'. It must never fall back to a
 * real status: on a pay document, guessing `approved` for a row the screen
 * could not classify is a claim that somebody signed it off.
 */
const TIMESHEET_STATUS_TONE: Record<string, string> = {
  approved: 'bg-fi-signal-green',
  exported: 'bg-fi-signal-green',
  draft: 'bg-fi-signal-amber',
  active: 'bg-fi-signal-amber',
};

/** Trucking pay periods carry the same four states, on their own calendar. */
const TRUCKING_PERIOD_TONE: Record<string, string> = {
  approved: 'bg-fi-signal-green',
  exported: 'bg-fi-signal-green',
  open: 'bg-fi-signal-amber',
  processing: 'bg-fi-signal-blue',
};

function StatusBadge({ status, tones }: { status: string | null | undefined; tones: Record<string, string> }) {
  const key = (status || '').toLowerCase();
  const tone = tones[key];
  return (
    <span
      className={`inline-flex items-center rounded-fi-pill border border-black/20 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-fi-signal-ink ${
        tone ?? 'bg-fi-signal-slate'
      }`}
    >
      {tone ? status : '—'}
    </span>
  );
}

export default function PayrollSummaryReport() {
  const navigate = useNavigate();
  // The TENANT's current month (companies.time_zone), not the device's. No
  // zone, no seeded range — the inputs start blank rather than guess.
  const timeZone = useTenantTimeZone();
  const now = tenantTodayDate(timeZone);
  const [startDate, setStartDate] = useState(now ? format(startOfMonth(now), 'yyyy-MM-dd') : '');
  const [endDate, setEndDate] = useState(now ? format(endOfMonth(now), 'yyyy-MM-dd') : '');

  /**
   * `GET /reports/payroll-summary`, api#794 — WHICH RETURNS A DIFFERENT SHAPE
   * FROM THE ONE THIS SCREEN USED TO READ, AND THAT IS THE POINT OF THE PR.
   *
   * The old handler summed `timesheets.gross_pay`, a column this product has
   * retired: `clockOut()` used to write `hours × base_rate` there and paid a
   * day rate as an hourly one, so it now writes NULL on purpose. The SUM was
   * therefore a sum of NULLs, `COALESCE` made it 0.00, and TNT — holding
   * $173,761.64 of generated pay — read $0.00 for every range that has ever
   * existed. fe#727 withdrew the catalog row rather than ship that.
   *
   * It now reads `employee_timesheets`, one row per (pay period × worker),
   * written by Generate and the approval feed — so it inherits their payroll
   * dating rules rather than re-deriving them.
   *
   * `LOAD_FAILURE_QUERY_OPTIONS`, and the query object is kept whole. This
   * route is PAY_READ, so a 403 is an ordinary outcome for a supervisor or a
   * field role — and this page answered one with "No payroll data for this
   * period.", a sentence about the company's payroll. Worse, a retrying 5xx
   * parks in `paused` where `error` is null and `isLoading` false, so a branch
   * on `error` alone would have looked correct against the refusal and stayed
   * broken for every server fault.
   */
  const payrollQuery = useQuery({
    queryKey: ['payroll-summary-report', startDate, endDate],
    queryFn: () =>
      api.reports
        .payrollSummary({ start_date: startDate, end_date: endDate })
        .then((r) => unwrap<PayrollSummaryReportData>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data, isLoading } = payrollQuery;
  const failure = queryLoadFailure(payrollQuery);

  /**
   * ARRIVED AND PENDING ARE TWO DIFFERENT FACTS AND NEED TWO BOOLEANS (fe#758).
   *
   * `!arrived` is true while the read is in flight AND true forever after it
   * failed. The band's `loading` flag draws a pulse, so gating the strip on it
   * leaves four figures claiming "a number is coming" beside a panel that has
   * already said the server could not be reached. `pending` is the in-flight
   * half only; every value is separately gated on `arrived` and passes `null`
   * — the one spelling `displayValue` and `fmtCurrency` turn into an em dash.
   */
  const arrived = !isLoading && !failure && data !== undefined && data !== null;
  const pending = !arrived && !failure;

  const operations = data?.operations;
  const trucking = data?.trucking ?? null;

  /* `useMemo`, not a bare `?? []`. A fresh `[]` each render makes the
     dependency of the grouping memo below change on every render, which is
     what `react-hooks/exhaustive-deps` reports — the memo would recompute
     every time and the warning ratchet fails the build on it. */
  const opsRows: OperationsPayrollRow[] = useMemo(() => operations?.rows ?? [], [operations]);
  const truckRows: TruckingPayrollRow[] = useMemo(() => trucking?.rows ?? [], [trucking]);

  /**
   * Operations rows in the order the server sent them, split into contiguous
   * groups by pay period.
   *
   * The server orders by `period_end`, `period_start`, name — so a period's
   * rows are already adjacent and this walks the list rather than bucketing it
   * into an object, which would lose that order.
   *
   * NO SUBTOTAL PER GROUP. A per-period figure is not one the endpoint returns,
   * and summing floats on screen to invent one is how a page ends up with two
   * answers to a money question. The figures that head this report are
   * `totals.committed` / `draft` / `all`, summed in integer cents on the server.
   */
  const opsGroups = useMemo(() => {
    const groups: { key: string; period_start: string; period_end: string; rows: OperationsPayrollRow[] }[] = [];
    for (const row of opsRows) {
      const last = groups[groups.length - 1];
      if (last && last.key === row.pay_period_id) {
        last.rows.push(row);
      } else {
        groups.push({ key: row.pay_period_id, period_start: row.period_start, period_end: row.period_end, rows: [row] });
      }
    }
    return groups;
  }, [opsRows]);

  function exportCSV() {
    const headers = [
      'Section', 'Period Start', 'Period End', 'Name', 'Role', 'Status', 'Committed',
      'Hours', 'Overtime Hours', 'Earnings', 'Overtime Earnings', 'Adjustments', 'Gross Pay',
    ];
    /* ONE FILE, TWO SECTIONS, A COLUMN THAT SAYS WHICH — and no combined total
       row anywhere in it. The screen refuses to add operations to trucking, and
       a spreadsheet that quietly did would undo that the moment somebody
       selected the Gross Pay column. Absent figures are EMPTY cells, never
       0.00: the trucking table records no earnings/overtime/adjustments split,
       and writing zeros there would state that those parts were nil. */
    const opsLines = opsRows.map((r) => [
      'Operations', r.period_start, r.period_end, r.name, r.role, r.timesheet_status,
      r.committed ? 'Yes' : 'No',
      r.total_hours ?? null, r.overtime_hours ?? null,
    ].map(csvCell).concat([
      csvMoney(r.earnings), csvMoney(r.overtime_earnings), csvMoney(r.adjustments), csvMoney(r.gross_pay),
    ]).join(','));
    const truckLines = truckRows.map((r) => [
      'Trucking', r.period_start, r.period_end, r.name, null, r.period_status,
      r.committed ? 'Yes' : 'No',
      r.regular_hours, r.ot_hours,
    ].map(csvCell).concat(['', '', '', csvMoney(r.gross_pay)]).join(','));

    const csv = [headers.map(csvCell).join(','), ...opsLines, ...truckLines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll-summary-${startDate}-to-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const hasRows = opsRows.length > 0 || truckRows.length > 0;
  const isEmpty = arrived && !hasRows;

  /**
   * FOUR STATS, AND ALL FOUR ARE OPERATIONS — WHICH SLOT 1's SUB-LINE SAYS.
   *
   * Four because four is the only stat count `PageHeaderBand`'s circle
   * clearance has been measured against across the reference pages; a 3-stat
   * strip moves column 3 under the ring and read 3.38–3.90 on Time Off
   * (fe#697). Slot 3's label is "Draft" — the shortest on the page, well under
   * the ~70px ceiling the circle's edge imposes there.
   *
   * THERE IS NO COMBINED FIGURE HERE, DELIBERATELY. Operations and trucking are
   * two payroll systems on two pay-period calendars; the endpoint that preceded
   * this one added them under one heading, so a tenant with both read a real
   * number plus a structural zero with nothing able to say which was which. The
   * band reports the operations half and names it; trucking gets its own panel
   * below, with its own totals and nothing summing the two.
   *
   * COMMITTED AND DRAFT ARE SEPARATE STATS FOR THE SAME REASON ONE LAYER IN.
   * TNT's all-time figure is $83,324.21 committed and $90,437.43 draft — a
   * reader taking a single "Payroll" number as payable would be out by more
   * than the payable half. Two stats, two labels, and the draft one says what
   * it is.
   *
   * `payrollGross` is what keeps `row_count: 0` out of the money slots: a
   * summed zero over no rows is not $0.00, it is a statement nobody made.
   */
  const bandStats = useMemo((): PageHeaderStat[] => {
    const committed = operations?.totals.committed;
    const draft = operations?.totals.draft;
    return [
      {
        icon: <Users className="size-4" />,
        value: displayValue(arrived ? operations?.worker_count ?? null : null),
        label: 'Workers',
        sub: 'Operations payroll',
        tone: 'accent',
        loading: pending,
      },
      {
        icon: <DollarSign className="size-4" />,
        value: fmt(arrived && committed ? payrollGross(committed) : null),
        label: 'Committed',
        sub: 'Approved or exported',
        tone: 'success',
        loading: pending,
      },
      {
        icon: <Clock className="size-4" />,
        value: fmt(arrived && draft ? payrollGross(draft) : null),
        label: 'Draft',
        sub: 'Not yet committed',
        tone: 'accent',
        loading: pending,
      },
      {
        icon: <CalendarRange className="size-4" />,
        value: displayValue(arrived ? operations?.periods.length ?? null : null),
        label: 'Periods',
        sub: 'Ending in this range',
        loading: pending,
      },
    ];
  }, [arrived, pending, operations]);

  return (
    <div data-testid="payroll-summary-report-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell renders the one global <Header />. Pages never do. */}
      <PageHeaderBand
        eyebrow="Reports"
        title="Payroll Summary"
        onBack={() => navigate('/reports')}
        backLabel="Back to reports"
        actions={
          hasRows ? (
            <button
              type="button"
              onClick={exportCSV}
              className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
            >
              <Download className="mr-1.5 inline size-4" aria-hidden="true" /> Export CSV
            </button>
          ) : undefined
        }
        stats={bandStats}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* The date range, in the body under the band. A pay period is in the
              range when its period END falls in it — the same end-dating the
              tickets follow, and it puts a period in exactly one range. */}
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted" htmlFor="payroll-start">
              From
            </label>
            <input
              id="payroll-start"
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="h-9 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm text-fieldiq-text focus:outline-none focus:ring-1 focus:ring-fi-amber"
            />
            <label className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted" htmlFor="payroll-end">
              To
            </label>
            <input
              id="payroll-end"
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="h-9 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm text-fieldiq-text focus:outline-none focus:ring-1 focus:ring-fi-amber"
            />
            <span className="text-[11px] text-fieldiq-muted">
              A pay period lands in this range when its END date does.
            </span>
          </div>

          {isLoading && (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-12 text-center">
              <span
                role="status"
                aria-label="Loading the payroll summary"
                className="mx-auto block h-7 w-40 animate-pulse rounded-fi-control bg-fieldiq-border"
              />
            </div>
          )}

          {/* AN ERROR IS NOT AN EMPTY STATE, AND A REFUSAL IS NOT "NO PAYROLL".
              This stands exactly where "No payroll data for this period." did. */}
          {!isLoading && failure && (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice
                error={queryFailureError(payrollQuery)}
                kind={failure}
                subject="the payroll summary"
                allowed="admins, office staff and payroll roles"
              />
            </div>
          )}

          {isEmpty && (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-12 text-center">
              <Users className="mx-auto mb-3 size-10 text-fieldiq-muted" aria-hidden="true" />
              <p className="text-sm text-fieldiq-text">No pay was generated for a period ending in this range.</p>
              <p className="mt-1 text-xs text-fieldiq-muted">
                That is a fact about the data, not about whether this range is right.
              </p>
            </div>
          )}

          {arrived && operations && opsRows.length > 0 && (
            <section
              aria-labelledby="payroll-operations-title"
              className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
            >
              <header className="border-b border-fieldiq-border px-4 py-4">
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Payroll</p>
                <h2 id="payroll-operations-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                  Operations
                </h2>
              </header>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-fieldiq-border">
                      <th scope="col" className="border-l-4 border-l-transparent px-4 py-3 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Name</th>
                      <th scope="col" className="px-4 py-3 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Role</th>
                      <th scope="col" className="px-4 py-3 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Status</th>
                      <th scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Hours</th>
                      <th scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Overtime</th>
                      <th scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Earnings</th>
                      <th scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">OT pay</th>
                      <th scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Adjustments</th>
                      <th scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Gross Pay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opsGroups.map((group) => (
                      /* A GROUP HEADER IS NOT A RECORD. One cell spanning every
                         column, carrying the same 4px TRANSPARENT rail as the
                         heading row so the columns under it stay aligned — a
                         coloured rail here would read as a status for the whole
                         period. No tabIndex, no click handler, no selection
                         tint. */
                      <React.Fragment key={group.key}>
                        <tr className="border-b border-fieldiq-border bg-fieldiq-dark/50">
                          <td colSpan={9} className="border-l-4 border-l-transparent px-4 py-2">
                            <span className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                              Pay period
                            </span>
                            <span className="ml-2 text-xs font-bold text-fieldiq-text">
                              {group.period_start} → {group.period_end}
                            </span>
                            <span className="ml-2 text-[11px] text-fieldiq-muted">
                              {group.rows.length} worker{group.rows.length === 1 ? '' : 's'}
                            </span>
                          </td>
                        </tr>
                        {group.rows.map((r) => (
                          <tr
                            key={`${r.pay_period_id}-${r.user_id}`}
                            className="border-b border-fieldiq-border last:border-b-0 hover:bg-fieldiq-dark/50"
                          >
                            <td className="border-l-4 border-l-transparent px-4 py-2.5 text-xs font-semibold text-fieldiq-text">
                              {displayValue(r.name)}
                            </td>
                            <td className="px-4 py-2.5 text-xs text-fieldiq-muted">{displayValue(r.role)}</td>
                            <td className="px-4 py-2.5">
                              <StatusBadge status={r.timesheet_status} tones={TIMESHEET_STATUS_TONE} />
                              {/* `committed: false` is money the office has not
                                  signed off. Said in words on the row, not left
                                  to the badge's hue — a reader taking a draft
                                  figure as payable is the failure this report
                                  was withdrawn for. */}
                              {!r.committed && (
                                <span className="mt-1 block text-[10px] text-fieldiq-muted">Not yet committed</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-right text-xs tabular-nums text-fieldiq-muted">{fmtHours(r.total_hours)}</td>
                            <td className="px-4 py-2.5 text-right text-xs tabular-nums text-fieldiq-muted">{fmtHours(r.overtime_hours)}</td>
                            <td className="px-4 py-2.5 text-right text-xs tabular-nums text-fieldiq-muted">{fmt(r.earnings)}</td>
                            <td className="px-4 py-2.5 text-right text-xs tabular-nums text-fieldiq-muted">{fmt(r.overtime_earnings)}</td>
                            <td className="px-4 py-2.5 text-right text-xs tabular-nums text-fieldiq-muted">{fmt(r.adjustments)}</td>
                            <td className="px-4 py-2.5 text-right text-xs font-semibold tabular-nums text-fieldiq-text">{fmt(r.gross_pay)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                  {/* THE THREE TOTALS AS THE SERVER SUMMED THEM, IN INTEGER
                      CENTS. Nothing on this screen adds a column up. */}
                  <tfoot>
                    <tr className="border-t border-fieldiq-border bg-fieldiq-dark/50">
                      <td colSpan={8} className="border-l-4 border-l-transparent px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-fieldiq-muted">
                        Committed — approved or exported
                      </td>
                      <td className="px-4 py-2 text-right text-sm font-extrabold tabular-nums text-fieldiq-text">
                        {fmt(payrollGross(operations.totals.committed))}
                      </td>
                    </tr>
                    <tr className="bg-fieldiq-dark/50">
                      <td colSpan={8} className="border-l-4 border-l-transparent px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-fieldiq-muted">
                        Not yet committed — draft
                      </td>
                      <td className="px-4 py-2 text-right text-sm font-extrabold tabular-nums text-fieldiq-text">
                        {fmt(payrollGross(operations.totals.draft))}
                      </td>
                    </tr>
                    <tr className="border-t border-fieldiq-border bg-fieldiq-dark/50">
                      <td colSpan={8} className="border-l-4 border-l-transparent px-4 py-2 text-right text-[11px] font-black uppercase tracking-wide text-fieldiq-text">
                        Operations total — committed and draft together
                      </td>
                      <td className="px-4 py-2 text-right text-sm font-extrabold tabular-nums text-fieldiq-text">
                        {fmt(payrollGross(operations.totals.all))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {/* A footer earns its place by carrying a count, a scope, a
                  permission or a timestamp. This is a COUNT and a SCOPE, and the
                  second sentence is the one a payroll reader can act on. */}
              <div className="border-t border-fieldiq-border px-4 py-3 text-[11px] text-fieldiq-muted">
                {operations.worker_count} worker{operations.worker_count === 1 ? '' : 's'} across{' '}
                {operations.periods.length} pay period{operations.periods.length === 1 ? '' : 's'}.{' '}
                {operations.totals.draft.row_count > 0
                  ? 'The total above includes draft pay, which nobody has approved yet — it is not a payable figure.'
                  : 'Every row above is committed pay.'}
              </div>
            </section>
          )}

          {/* TRUCKING IS RENDERED ONLY WHEN THE SERVER SENT IT, and `null` is
              the server saying the tenant has no trucking vertical — not that it
              has no trucking pay. An empty section drawn over a `null` would be
              the same mistake the band's old "Trucking Staff 0" made: a fact
              about FieldIQ's schema presented as a fact about the company.

              The gate is the payload, not `hasVertical`. One question, one
              answer, and it is the answer the figures came from. */}
          {arrived && trucking && (
            <section
              aria-labelledby="payroll-trucking-title"
              className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
            >
              <header className="border-b border-fieldiq-border px-4 py-4">
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Payroll</p>
                <h2 id="payroll-trucking-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                  Trucking
                </h2>
                <p className="mt-1 flex items-start gap-1.5 text-[11px] text-fieldiq-muted">
                  <Truck className="mt-px size-3.5 shrink-0 text-fieldiq-accent" aria-hidden="true" />
                  <span>
                    Trucking pay runs on its own pay-period calendar. It is reported beside Operations and is
                    never added to it — there is no company-wide payroll total on this page, on purpose.
                  </span>
                </p>
              </header>
              {truckRows.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-fieldiq-text">
                  No driver pay was generated for a trucking period ending in this range.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-fieldiq-border">
                        <th scope="col" className="border-l-4 border-l-transparent px-4 py-3 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Driver</th>
                        <th scope="col" className="px-4 py-3 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Pay period</th>
                        <th scope="col" className="px-4 py-3 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Status</th>
                        <th scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Regular</th>
                        <th scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">OT</th>
                        <th scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Double OT</th>
                        <th scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Gross Pay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {truckRows.map((r) => (
                        <tr
                          key={`${r.pay_period_id}-${r.driver_id}`}
                          className="border-b border-fieldiq-border last:border-b-0 hover:bg-fieldiq-dark/50"
                        >
                          <td className="border-l-4 border-l-transparent px-4 py-2.5 text-xs font-semibold text-fieldiq-text">
                            {displayValue(r.name)}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-fieldiq-muted">{r.period_start} → {r.period_end}</td>
                          <td className="px-4 py-2.5">
                            <StatusBadge status={r.period_status} tones={TRUCKING_PERIOD_TONE} />
                            {!r.committed && (
                              <span className="mt-1 block text-[10px] text-fieldiq-muted">Not yet committed</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right text-xs tabular-nums text-fieldiq-muted">{fmtHours(r.regular_hours)}</td>
                          <td className="px-4 py-2.5 text-right text-xs tabular-nums text-fieldiq-muted">{fmtHours(r.ot_hours)}</td>
                          <td className="px-4 py-2.5 text-right text-xs tabular-nums text-fieldiq-muted">{fmtHours(r.double_ot_hours)}</td>
                          <td className="px-4 py-2.5 text-right text-xs font-semibold tabular-nums text-fieldiq-text">{fmt(r.gross_pay)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-fieldiq-border bg-fieldiq-dark/50">
                        <td colSpan={6} className="border-l-4 border-l-transparent px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-fieldiq-muted">
                          Committed — approved or exported
                        </td>
                        <td className="px-4 py-2 text-right text-sm font-extrabold tabular-nums text-fieldiq-text">
                          {fmt(payrollGross(trucking.totals.committed))}
                        </td>
                      </tr>
                      <tr className="bg-fieldiq-dark/50">
                        <td colSpan={6} className="border-l-4 border-l-transparent px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-fieldiq-muted">
                          Not yet committed — draft
                        </td>
                        <td className="px-4 py-2 text-right text-sm font-extrabold tabular-nums text-fieldiq-text">
                          {fmt(payrollGross(trucking.totals.draft))}
                        </td>
                      </tr>
                      <tr className="border-t border-fieldiq-border bg-fieldiq-dark/50">
                        <td colSpan={6} className="border-l-4 border-l-transparent px-4 py-2 text-right text-[11px] font-black uppercase tracking-wide text-fieldiq-text">
                          Trucking total — committed and draft together
                        </td>
                        <td className="px-4 py-2 text-right text-sm font-extrabold tabular-nums text-fieldiq-text">
                          {fmt(payrollGross(trucking.totals.all))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
              <div className="border-t border-fieldiq-border px-4 py-3 text-[11px] text-fieldiq-muted">
                {trucking.driver_count} driver{trucking.driver_count === 1 ? '' : 's'} across{' '}
                {trucking.periods.length} trucking pay period{trucking.periods.length === 1 ? '' : 's'}.
              </div>
            </section>
          )}
        </div>
      </section>
    </div>
  );
}
